import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Tabs,
  Tab,
  Badge,
  Card,
  CardContent,
  Avatar,
  Button,
  Stack,
  CircularProgress,
  useTheme,
  alpha,
  Divider,
  Paper,
  Fade,
} from "@mui/material";
import {
  NotificationsActive,
  School,
  Group,
  CheckCircle,
  NotificationsOff,
  History,
  PersonAdd,
  Groups,
  Description,
} from "@mui/icons-material";
import axios from "axios";
import Header from "../Components/Header";
import StudentSidebar from "./StudentSdebar";

const Notification = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const [activeTab, setActiveTab] = useState("all");
  const [sidebarExpanded, setSidebarExpanded] = useState(true);
  const token = localStorage.getItem("jwttoken");
  const url = "https://tdtlworld.com/dss-kaas-backend/";

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const [lmsResponse, socialResponse] = await Promise.all([
        axios.get(`${url}lms/user/notifications/`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${url}socialapp/notifications/`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      const lmsNotifications = lmsResponse.data.map((notif) => ({
        ...notif,
        source: "LMS",
        is_read: true,
      }));

      const socialNotifications = socialResponse.data.map((notif) => ({
        ...notif,
        source: "SocialApp",
        message: formatSocialMessage(notif),
      }));

      const mergedNotifications = [...lmsNotifications, ...socialNotifications].sort(
        (a, b) => new Date(b.created_at || b.date_sent) - new Date(a.created_at || a.date_sent),
      );

      setNotifications(mergedNotifications);
      setUnreadCount(socialNotifications.filter((n) => !n.is_read).length);
    } catch (error) {
      console.error("Error fetching notifications:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatSocialMessage = (notif) => {
    switch (notif.type) {
      case "group_join_request":
        return `${notif.sender} requested to join a group.`;
      case "group_join_approved":
        return `Your request to join the group was approved.`;
      case "group_join":
        return `You were added to the group.`;
      default:
        return notif.message || "You have a new notification.";
    }
  };

  const markAsRead = async (id) => {
    try {
      await axios.post(
        `${url}socialapp/notifications/${id}/read/`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      setNotifications((prev) => prev.map((notif) => (notif.id === id ? { ...notif, is_read: true } : notif)));
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  const filteredNotifications = notifications.filter((notif) => {
    if (activeTab === "all") return true;
    if (activeTab === "lms") return notif.source === "LMS";
    if (activeTab === "social") return notif.source === "SocialApp";
    if (activeTab === "unread") return !notif.is_read;
    if (activeTab === "read") return notif.is_read;
    return true;
  });

  const getNotificationStyles = (notification) => {
    if (notification.source === "LMS") {
      return { icon: <School />, color: "#3953A3" };
    } else {
      switch (notification.type) {
        case "group_join_request":
          return { icon: <PersonAdd />, color: "#B8529E" };
        case "group_join_approved":
          return { icon: <CheckCircle />, color: "#22c55e" };
        case "group_join":
          return { icon: <Groups />, color: "#6366f1" };
        default:
          return { icon: <NotificationsActive />, color: "#D4AF37" };
      }
    }
  };

  const getTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);
    if (seconds < 60) return "just now";
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden', bgcolor: 'background.default' }}>
      <Header />
      <Box sx={{ display: 'flex', flexGrow: 1, overflow: 'hidden' }}>
        <StudentSidebar onToggle={setSidebarExpanded} />
        
        <Box 
          component="main" 
          sx={{ 
            flexGrow: 1, 
            p: { xs: 2, md: 4 },
            width: sidebarExpanded ? 'calc(100% - 260px)' : 'calc(100% - 80px)',
            transition: theme.transitions.create('width', { duration: 300 }),
            height: 'calc(100vh - 64px)',
            overflowY: 'auto'
          }}
        >
          <Container maxWidth="md">
            <Box sx={{ mb: 4, textAlign: 'center' }}>
              <Typography variant="h3" fontWeight={800} gutterBottom>Notifications</Typography>
              <Typography variant="body1" color="text.secondary">Stay updated with your learning journey and social activity.</Typography>
            </Box>

            <Tabs 
              value={activeTab} 
              onChange={(e, v) => setActiveTab(v)}
              centered
              sx={{ 
                mb: 4, 
                bgcolor: 'background.paper', 
                borderRadius: '50px', 
                p: 0.5,
                boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                '& .MuiTabs-indicator': { height: '100%', borderRadius: '50px', zIndex: 0, opacity: 0.1 },
                '& .MuiTab-root': { zIndex: 1, fontWeight: 700, minHeight: 48, borderRadius: '50px', transition: '0.3s' }
              }}
            >
              <Tab label="All" value="all" />
              <Tab label="LMS" value="lms" />
              <Tab label="Social" value="social" />
              <Tab 
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    Unread
                    {unreadCount > 0 && <Badge badgeContent={unreadCount} color="error" sx={{ '& .MuiBadge-badge': { fontWeight: 800 } }} />}
                  </Box>
                } 
                value="unread" 
              />
              <Tab label="Read" value="read" />
            </Tabs>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
                  <CircularProgress sx={{ color: '#D4AF37' }} />
                </Box>
              ) : filteredNotifications.length > 0 ? (
                filteredNotifications.map((notif, index) => {
                  const { icon, color } = getNotificationStyles(notif);
                  return (
                    <Fade in={true} timeout={500 + index * 100} key={index}>
                      <Card sx={{ 
                        borderRadius: '20px', 
                        border: '1px solid', 
                        borderColor: notif.is_read ? 'divider' : alpha(color, 0.3),
                        bgcolor: notif.is_read ? 'background.paper' : alpha(color, 0.02),
                        transition: '0.3s',
                        '&:hover': { transform: 'translateX(5px)', boxShadow: theme.shadows[2] }
                      }}>
                        <CardContent sx={{ display: 'flex', p: 2.5, alignItems: 'flex-start', gap: 2 }}>
                          <Avatar sx={{ 
                            bgcolor: alpha(color, 0.1), 
                            color: color, 
                            width: 50, 
                            height: 50,
                            borderRadius: '15px'
                          }}>
                            {icon}
                          </Avatar>
                          <Box sx={{ flexGrow: 1 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                              <Typography variant="subtitle2" fontWeight={800} color={color}>
                                {notif.source === "LMS" ? "LEARNING MANAGEMENT" : "SOCIAL NETWORK"}
                              </Typography>
                              <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary', fontWeight: 600 }}>
                                <History sx={{ fontSize: 14 }} /> {getTimeAgo(notif.created_at || notif.date_sent)}
                              </Typography>
                            </Box>
                            <Typography variant="body1" fontWeight={500} sx={{ mb: 1, color: 'text.primary' }}>
                              {notif.message || notif.text}
                            </Typography>
                            {notif.source === "SocialApp" && !notif.is_read && (
                              <Button 
                                size="small" 
                                variant="outlined" 
                                onClick={() => markAsRead(notif.id)}
                                sx={{ borderRadius: '50px', fontWeight: 700, textTransform: 'none' }}
                              >
                                Mark as Read
                              </Button>
                            )}
                          </Box>
                        </CardContent>
                      </Card>
                    </Fade>
                  );
                })
              ) : (
                <Box sx={{ textAlign: 'center', py: 10, bgcolor: 'background.paper', borderRadius: '30px', border: '1px dashed', borderColor: 'divider' }}>
                  <NotificationsOff sx={{ fontSize: '4rem', color: 'text.disabled', mb: 2 }} />
                  <Typography variant="h6" fontWeight={700}>All caught up!</Typography>
                  <Typography variant="body2" color="text.secondary">No new notifications to show right now.</Typography>
                </Box>
              )}
            </Box>
          </Container>
        </Box>
      </Box>
    </Box>
  );
};

export default Notification;
